
import { defineComponent, ref, computed, onMounted } from 'vue';

interface Project {
  id: string;
  name: string;
  fullName: string;
  description: string;
  type: string;
  language: string;
  stars: number;
  forks: number;
  url: string;
  websiteUrl?: string;
  topics: string[];
  role: string;
  organization: string | null;
  startDate: string;
  endDate: string;
  logoUrl?: string;
}

interface ProjectsData {
  projects: Project[];
}

export default defineComponent({
  name: 'LocalProjects',
  props: {
    apiUrl: {
      type: String,
      default: '/data/projects.json'
    },
    branding: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    // Reactive data
    const loading = ref(true);
    const error = ref<string | null>(null);
    const projects = ref<Project[]>([]);
    const showAll = ref(false);
    const initialDisplayCount = 3;

    // Computed properties
    const showBranding = computed(() => props.branding);

    const displayedProjects = computed(() => {
      if (showAll.value) {
        return projects.value;
      }
      return projects.value.slice(0, initialDisplayCount);
    });

    const formatNumber = (num: number): string => {
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'k';
      }
      return num.toString();
    };

    const getLanguageClass = (language: string): string => {
      const languageMapping: Record<string, string> = {
        'go': 'lang-go',
        'typescript': 'lang-typescript',
        'javascript': 'lang-javascript',
        'dart': 'lang-dart',
        'python': 'lang-python',
        'rust': 'lang-rust',
        'java': 'lang-java',
        'c': 'lang-c',
        'cpp': 'lang-cpp',
        'c++': 'lang-cpp',
        'ruby': 'lang-ruby',
        'solidity': 'lang-solidity'
      };

      return languageMapping[language.toLowerCase()] || 'lang-other';
    };

    const toggleShowAll = () => {
      showAll.value = !showAll.value;
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;

        const response = await fetch(props.apiUrl);
        if (!response.ok) {
          throw new Error(`Failed to load projects data: ${response.status}`);
        }

        const data: ProjectsData = await response.json();
        projects.value = data.projects;

      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load projects data';
        console.error('Projects Data Error:', err);
      } finally {
        loading.value = false;
      }
    };

    // Lifecycle
    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      error,
      projects,
      showAll,
      initialDisplayCount,
      displayedProjects,
      showBranding,
      formatNumber,
      getLanguageClass,
      toggleShowAll
    };
  }
});
